/* ---------------------------------------------------------------------------
   Moving between the four applications.

   Within one app, navigation is `actions.go` and a repaint. Between apps it is
   an address: all four share one page (see src/core/apps.ts), and the app is the
   first segment of the hash, so a link to another app is a plain `#/…` anchor
   that the router reads like any other. This module is the whole of that seam,
   and it is a module of its own for one reason: it needs the router's `encode`
   to build the address, and the router is built on the store, so the store
   cannot reach back for it. Nothing imports
   this but the controls that offer the move — the nav pane's rail, the home
   page's cards and table, and the cross-app links on a structure screen.

   ONE RULE RUNS THROUGH ALL OF IT: THE STRUCTURE TRAVELS. Leaving a bridge's
   record for its programme opens THAT BRIDGE's programme. Anything else makes
   the rail a way of losing your place, and the reason anybody presses it from a
   record is that they are still thinking about the same structure.
   --------------------------------------------------------------------------- */

import { appById, appForShell, appOf, owns, type AppDef, type AppId } from './apps.ts';
import { encode } from './router.ts';
import { actions, store, type State } from './store.ts';
import type { Screen } from '../data/types.ts';

/** The address of a screen in an app — this one unless another is named. */
export function hashFor(s: Readonly<State>, screen: Screen, app: AppId = s.app): string {
  return encode({ ...s, app, screen });
}

/**
 * The link for a screen, in whichever app draws it: this app when it does, the
 * first that does otherwise. Always a hash — every app is on this page.
 */
export function hrefFor(s: Readonly<State>, screen: Screen): string {
  const here = appForShell(s.shell);
  if (owns(here, screen)) return hashFor(s, screen, here.id);
  const target = appOf(screen, here);
  return hashFor(s, screen, target ? target.id : here.id);
}

/**
 * The link into an app, carrying the selected structure: its structure screen
 * for that structure where it has one, its landing screen where it does not.
 * THE STRUCTURE TRAVELS — see the header. The explorer's page does not, from
 * the start page: an app opened from home opens on Bridge info.
 */
export function appHref(s: Readonly<State>, id: AppId): string {
  const target = appById(id);
  if (s.app === 'home') s = { ...s, detailView: 'inventory' };
  if (target.separate) return hashFor(s, target.landing, id);
  return hashFor(s, target.structure ?? target.landing, id);
}

/** Go to an app, from a control that is not a link. */
export function openApp(id: AppId): void {
  const target = appById(id);
  actions.openApp(id, target.structure ?? target.landing);
}

/**
 * Open a structure in an app: select it, then that app's structure screen.
 * An app with no structure screen (the start page, Bridge Rating) is opened at
 * its landing screen with the structure selected.
 */
export function openBridgeIn(id: AppId, bridgeId: string): void {
  const target = appById(id);
  actions.selectBridge(bridgeId);
  if (store.get().shell === 'field') {
    if (target.structure) actions.go(target.structure);
    return;
  }
  actions.openApp(id, target.structure ?? target.landing);
}

/**
 * The bridge dashboard of a structure (LC-30), which belongs to the start page
 * rather than to any one application: its address, and the way there from a
 * control that is not a link.
 */
export function dashboardHref(s: Readonly<State>, bridgeId: string = s.selectedId): string {
  return hashFor({ ...s, selectedId: bridgeId }, 'board', 'home');
}

export function openDashboard(bridgeId: string): void {
  actions.selectBridge(bridgeId);
  actions.openApp('home', 'board');
}

export function otherApps(here: AppDef, all: readonly AppDef[]): AppDef[] {
  return all.filter((a) => a.id !== here.id);
}
